import { useEffect, useState } from "react";
import { Building2, Save, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

const emptyGym = { name: "", location: "", logo_base64: "" };
const MAX_CERT = 10 * 1024 * 1024;

export default function GymsAdminPage() {
  const [gyms, setGyms] = useState([]);
  const [form, setForm] = useState(emptyGym);
  // Nur bei "Neues Gym": zwischengehaltener Zertifikat-Upload, um Gym und PDF
  // in einem einzigen Speichern-Vorgang anzulegen. Beim Bearbeiten läuft das
  // Zertifikat weiterhin über die separaten Endpunkte.
  const [pendingCert, setPendingCert] = useState(null); // { file_name, data_base64, size_bytes }
  const [pendingCertError, setPendingCertError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = async () => { try { const r = await api.get("/gyms"); setGyms(r.data); } catch (e) { setMsg(apiError(e)); } };
  useEffect(() => { load(); }, []);
  const startEdit = (g) => { setEditingId(g.id); setForm({ name: g.name, location: g.location, logo_base64: g.logo_base64 || "" }); setPendingCert(null); setPendingCertError(""); setMsg(""); };
  const cancelEdit = () => { setEditingId(null); setForm(emptyGym); setPendingCert(null); setPendingCertError(""); };
  const pickLogo = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setMsg(`Logo zu groß: ${(file.size/1024/1024).toFixed(2)} MB — max. 2 MB.`); return; }
    const r = new FileReader(); r.onload = () => setForm({ ...form, logo_base64: r.result }); r.readAsDataURL(file);
  };
  // PDF für "Neues Gym" vormerken (wird beim Save gemeinsam mit dem Gym gespeichert).
  const pickPendingCert = (file) => {
    setPendingCertError("");
    if (!file) { setPendingCert(null); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) { setPendingCertError("Nur PDF-Dateien für die Urkunde."); return; }
    if (file.size > MAX_CERT) { setPendingCertError(`Datei zu groß: ${(file.size/1024/1024).toFixed(2)} MB — max. 10 MB.`); return; }
    const reader = new FileReader();
    reader.onload = () => setPendingCert({ file_name: file.name, data_base64: reader.result, size_bytes: file.size });
    reader.onerror = () => setPendingCertError("Datei konnte nicht gelesen werden.");
    reader.readAsDataURL(file);
  };
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setMsg("");
    try {
      if (editingId) {
        await api.put(`/gyms/${editingId}`, form);
        setMsg("✓ Gym aktualisiert.");
      } else {
        const r = await api.post("/gyms", form);
        const newId = r.data.id;
        // Falls im Anlegen-Schritt eine Urkunde ausgewählt war, sofort hochladen.
        if (pendingCert) {
          try {
            await api.post(`/gyms/${newId}/certificate`, { file_name: pendingCert.file_name, data_base64: pendingCert.data_base64 });
            setMsg("✓ Gym samt Urkunde angelegt.");
          } catch (certErr) {
            setMsg("✓ Gym angelegt — Urkunden-Upload fehlgeschlagen: " + apiError(certErr));
          }
        } else {
          setMsg("✓ Gym angelegt.");
        }
        setEditingId(newId);
        setPendingCert(null);
      }
      await load();
    } catch (err) { setMsg(apiError(err)); } finally { setBusy(false); }
  };
  const del = async (g) => {
    if (!window.confirm(`Gym „${g.name}" endgültig löschen?`)) return;
    try { await api.delete(`/gyms/${g.id}`); setMsg("✓ Gelöscht."); if (editingId === g.id) cancelEdit(); await load(); }
    catch (err) { setMsg(apiError(err)); }
  };
  const uploadCert = async (e) => {
    const file = e.target.files?.[0]; if (!file || !editingId) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { setMsg("Nur PDF-Dateien für die Urkunde."); return; }
    if (file.size > MAX_CERT) { setMsg(`Datei zu groß: ${(file.size/1024/1024).toFixed(2)} MB — max. 10 MB.`); return; }
    setBusy(true);
    try {
      const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = () => rej(new Error("Lesefehler")); r.readAsDataURL(file); });
      const r = await api.post(`/gyms/${editingId}/certificate`, { file_name: file.name, data_base64: dataUrl });
      setMsg("✓ " + r.data.message);
      await load();
    } catch (err) { setMsg(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const deleteCert = async () => {
    if (!editingId || !window.confirm("Urkunde löschen?")) return;
    try { await api.delete(`/gyms/${editingId}/certificate`); setMsg("✓ Urkunde entfernt."); await load(); } catch (err) { setMsg(apiError(err)); }
  };
  const currentGym = gyms.find((g) => g.id === editingId);
  return <div className="content-frame" data-testid="gyms-admin-page">
    <header className="page-heading"><div><p className="eyebrow">ADMIN-BEREICH</p><h1>WBC Gyms pflegen</h1><p className="muted">Name, Ort, Logo und WBC-Urkunde für jedes Gym. Logos in Übersicht groß dargestellt.</p></div></header>
    {msg && <div className={msg.startsWith("✓") ? "success-notice" : "form-error"} data-testid="gyms-admin-message" style={{marginBottom:12}}>{msg}</div>}
    <div style={{display:"grid",gridTemplateColumns:"320px 1fr",gap:24}}>
      <aside style={{background:"#111",padding:16,borderRadius:8,maxHeight:600,overflowY:"auto"}}>
        <button type="button" className="button primary" onClick={cancelEdit} data-testid="gyms-new-button" style={{width:"100%",marginBottom:12}}>+ Neues Gym</button>
        {gyms.map((g) => <div key={g.id} onClick={() => startEdit(g)} data-testid={`gyms-admin-row-${g.id}`} style={{padding:10,cursor:"pointer",borderRadius:6,background:editingId===g.id?"#1a2a1a":"transparent",marginBottom:6,display:"flex",gap:10,alignItems:"center"}}>
          {g.logo_base64 ? <img src={g.logo_base64} alt="" style={{width:40,height:40,objectFit:"contain",borderRadius:4,background:"#000"}} /> : <div style={{width:40,height:40,background:"#000",borderRadius:4,display:"flex",alignItems:"center",justifyContent:"center"}}><Building2 size={20} color="#555" /></div>}
          <div style={{flex:1,minWidth:0}}><b style={{fontSize:13,display:"block"}}>{g.name}</b><small className="muted">{g.location}</small></div>
          <button type="button" className="mini-btn" style={{background:"#8a2a2a",color:"#fff",border:"none"}} onClick={(e) => { e.stopPropagation(); del(g); }} data-testid={`gyms-delete-${g.id}`}><Trash2 size={12} /></button>
        </div>)}
      </aside>
      <form onSubmit={save} style={{background:"#111",padding:20,borderRadius:8}}>
        <b style={{fontSize:14,color:"#f4d33e",letterSpacing:0.6}}>{editingId ? "GYM BEARBEITEN" : "NEUES GYM"}</b>
        <div className="form-grid" style={{marginTop:12}}>
          <label>Name<input required value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="gym-form-name" /></label>
          <label>Ort<input required value={form.location} maxLength={120} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="gym-form-location" /></label>
          <label>Logo (Bild, max. 2 MB)<input type="file" accept="image/*" onChange={(e) => pickLogo(e.target.files?.[0])} data-testid="gym-form-logo" /></label>
          {!editingId && <label>WBC-Urkunde (PDF, max. 10 MB, optional)<input type="file" accept="application/pdf,.pdf" onChange={(e) => pickPendingCert(e.target.files?.[0])} data-testid="gym-form-cert-new" /></label>}
        </div>
        {form.logo_base64 && <div style={{marginTop:12,display:"flex",alignItems:"center",gap:12}}><img src={form.logo_base64} alt="" style={{width:120,height:120,objectFit:"contain",background:"#000",borderRadius:6}} data-testid="gym-form-logo-preview" /><button type="button" className="button ghost" onClick={() => setForm({ ...form, logo_base64: "" })}>Logo entfernen</button></div>}
        {!editingId && pendingCert && <div style={{marginTop:12,padding:10,background:"#0a1a2a",borderRadius:6,display:"flex",alignItems:"center",gap:10}} data-testid="gym-form-cert-preview">
          <span style={{fontSize:13}}>📄 <b>{pendingCert.file_name}</b> · {(pendingCert.size_bytes/1024/1024).toFixed(2)} MB (wird beim Speichern hochgeladen)</span>
          <button type="button" className="button ghost" onClick={() => setPendingCert(null)} data-testid="gym-form-cert-remove">Entfernen</button>
        </div>}
        {!editingId && pendingCertError && <div className="form-error" data-testid="gym-form-cert-error" style={{marginTop:8}}>{pendingCertError}</div>}
        <div style={{display:"flex",gap:8,marginTop:16}}>
          <button type="submit" className="button primary" disabled={busy} data-testid="gym-form-save"><Save size={16} /> Speichern</button>
          {editingId && <button type="button" className="button ghost" onClick={cancelEdit} data-testid="gym-form-cancel"><X size={16} /> Abbrechen</button>}
        </div>
        {editingId && <div style={{marginTop:24,paddingTop:20,borderTop:"1px solid rgba(255,255,255,.1)"}}>
          <b style={{fontSize:14,color:"#f4d33e",letterSpacing:0.6}}>WBC-URKUNDE (PDF, max. 10 MB)</b>
          {currentGym?.has_certificate ? <div style={{marginTop:8,padding:12,background:"#0a1a2a",borderRadius:6}}>
            <p style={{fontSize:13,margin:"0 0 8px"}}><b>{currentGym.certificate_file_name}</b> · {(currentGym.certificate_size_bytes/1024/1024).toFixed(2)} MB</p>
            <button type="button" className="button" style={{background:"#8a2a2a",color:"#fff"}} onClick={deleteCert} data-testid="gym-cert-delete"><Trash2 size={14} /> Urkunde entfernen</button>
          </div> : <p className="muted" style={{fontSize:12}}>Noch keine Urkunde. PDF auswählen:</p>}
          <label style={{display:"block",marginTop:8}}><input type="file" accept="application/pdf,.pdf" onChange={uploadCert} disabled={busy} data-testid="gym-cert-upload" /></label>
        </div>}
      </form>
    </div>
  </div>;
}
